'use client';

import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

interface HubSpotFormModalProps {
  formId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  portalId?: string;
  region?: string;
}

export function HubSpotFormModal({
  formId,
  open,
  onOpenChange,
  title = 'Get in Touch',
  description = "Fill out the form below and we'll get back to you shortly.",
  portalId = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID || '',
  region = process.env.NEXT_PUBLIC_HUBSPOT_REGION || 'na1',
}: HubSpotFormModalProps) {
  const [loading, setLoading] = React.useState(true);
  const formContainerRef = React.useRef<HTMLDivElement>(null);
  const onOpenChangeRef = React.useRef(onOpenChange);

  React.useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  React.useEffect(() => {
    if (!open || !formId || !portalId) return;

    setLoading(true);

    const renderForm = () => {
      // Delay to ensure Dialog portal content is mounted in the DOM
      const timer = setTimeout(() => {
        if (!window.hbspt || !formContainerRef.current) return;

        // Clear any previously rendered form
        formContainerRef.current.innerHTML = '';

        try {
          window.hbspt.forms.create({
            region: region,
            portalId: portalId,
            formId: formId,
            target: `#hubspot-form-container-${formId}`,
            css: '',
            formInstanceId: `${formId}-${Date.now()}`,
            onFormReady: () => {
              setLoading(false);

              setTimeout(() => {
                const container = document.getElementById(`hubspot-form-container-${formId}`);
                if (container) {
                  const formElement = container.querySelector('form');
                  if (formElement) {
                    formElement.setAttribute('id', formId);
                    formElement.setAttribute('data-hubspot-form-id', formId);
                    formElement.setAttribute('data-portal-id', portalId);

                    const action = formElement.getAttribute('action');
                    if (!action || !action.includes(formId)) {
                      formElement.setAttribute(
                        'action',
                        `https://forms.hsforms.com/submissions/v3/public/submit/formsnext/multipart/${portalId}/${formId}`
                      );
                    }
                  }

                  // Restrict file uploads to PDF only
                  const fileInputs =
                    container.querySelectorAll<HTMLInputElement>('input[type="file"]');
                  fileInputs.forEach((input) => {
                    input.setAttribute('accept', '.pdf');

                    // Add help text below the file input
                    const helpText = document.createElement('p');
                    helpText.textContent = 'PDF files only';
                    helpText.style.cssText = 'margin:4px 0 0;font-size:13px;color:#6b7280;';
                    input.parentElement?.insertBefore(helpText, input.nextSibling);

                    input.addEventListener('change', () => {
                      const file = input.files?.[0];
                      if (file) {
                        const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
                        if (ext !== '.pdf') {
                          input.value = '';
                          input.setCustomValidity('Please upload a PDF file');
                          input.reportValidity();
                        } else {
                          input.setCustomValidity('');
                        }
                      }
                    });
                  });
                }
              }, 100);
            },
            onFormSubmitted: () => {
              setTimeout(() => {
                onOpenChangeRef.current(false);
              }, 2000);
            },
          });
        } catch {
          setLoading(false);
        }
      }, 50);

      return timer;
    };

    let timer: ReturnType<typeof setTimeout> | undefined;

    // If HubSpot script is already loaded, render the form
    if (window.hbspt) {
      timer = renderForm();
    } else {
      // Check if the script tag already exists (another modal instance loaded it)
      const existingScript = document.querySelector<HTMLScriptElement>(
        'script[src="https://js.hsforms.net/forms/embed/v2.js"]'
      );

      if (existingScript) {
        const onLoad = () => {
          timer = renderForm();
        };
        existingScript.addEventListener('load', onLoad);
        return () => existingScript.removeEventListener('load', onLoad);
      }

      // Load HubSpot forms script once
      const script = document.createElement('script');
      script.src = 'https://js.hsforms.net/forms/embed/v2.js';
      script.async = true;
      script.charset = 'utf-8';
      script.onload = () => {
        timer = renderForm();
      };
      script.onerror = () => {
        setLoading(false);
      };
      document.body.appendChild(script);
    }

    const containerEl = formContainerRef.current;
    return () => {
      clearTimeout(timer);
      if (containerEl) {
        containerEl.innerHTML = '';
      }
    };
  }, [open, formId, portalId, region]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-8 sm:p-10">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="relative min-h-[300px] mt-4">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          <div
            id={`hubspot-form-container-${formId}`}
            ref={formContainerRef}
            className={`hubspot-form-styled ${loading ? 'opacity-0' : 'opacity-100 transition-opacity'}`}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// TypeScript declaration for HubSpot
declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (options: {
          region: string;
          portalId: string;
          formId: string;
          target: string;
          formInstanceId?: string;
          css?: string;
          cssClass?: string;
          onFormReady?: () => void;
          onFormSubmitted?: () => void;
        }) => void;
      };
    };
  }
}
